import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 font-heading mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-4">该漫画不存在或已被移除</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedComics = comics.filter((c) => c.id !== comic.id).slice(0, 3);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: comic.category, href: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />
        </div>

        <article className="px-4 md:px-6 py-6 md:py-10" aria-label={`${comic.title} 漫画详情`}>
          <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
            <div className="w-full lg:w-[340px] flex-shrink-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  title={`${comic.title} - ${comic.category}韩国漫画`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="mt-4 flex gap-3">
                <Link
                  to={`/comic/${comic.slug}`}
                  className="flex-1 bg-primary-500 text-white dark:text-foreground-950 text-sm font-medium py-3 rounded-md text-center hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  开始阅读
                </Link>
              </div>
            </div>

            <div className="flex-1">
              <span className="text-xs text-foreground-500 font-medium">
                {comic.category}
              </span>
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mt-1 mb-6">
                {comic.title}
              </h1>

              <div className="text-foreground-600 text-sm leading-relaxed mb-6">
                <p>{comic.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div>
                  <span className="text-xs text-foreground-500">作者</span>
                  <p className="text-sm font-medium text-foreground-900">{comic.author}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">画师</span>
                  <p className="text-sm font-medium text-foreground-900">{comic.artist}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">状态</span>
                  <p className="text-sm font-medium">
                    <span className={comic.status === '连载中' ? 'text-primary-600' : 'text-accent-600'}>
                      {comic.status}
                    </span>
                  </p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">评分</span>
                  <p className="text-sm font-medium text-foreground-900">
                    <i className="ri-star-fill text-yellow-500 text-xs w-3 h-3 inline-flex items-center justify-center mr-0.5"></i>
                    {comic.rating}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">阅读量</span>
                  <p className="text-sm font-medium text-foreground-900">
                    {comic.views > 1000000
                      ? `${(comic.views / 1000000).toFixed(1)}M`
                      : `${(comic.views / 10000).toFixed(0)}万`}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">最近更新</span>
                  <p className="text-sm font-medium text-foreground-900">{comic.updateDate}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-secondary-100 text-secondary-700 text-xs px-3 py-1 rounded-md"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        <section className="px-4 md:px-6 py-6 md:py-10 bg-background-100" aria-label="章节列表">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">
            章节列表
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {comic.chapters.map((ch) => (
              <Link
                key={ch.id}
                to={`/comic/${comic.slug}`}
                className="flex items-center justify-between bg-background-50 rounded-lg p-4 hover:bg-primary-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xs text-foreground-500 font-medium">第{ch.number}话</span>
                  <span className="text-sm text-foreground-900 font-medium">{ch.title}</span>
                </div>
                <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.updateDate}</span>
              </Link>
            ))}
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="px-4 md:px-6 py-10 md:py-14" aria-label="相关推荐">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-6">
              {relatedComics.map((c) => (
                <ComicCard key={c.id} comic={c} variant="featured" />
              ))}
            </div>
          </section>
        )}

        <section className="py-14 md:py-20 px-4 md:px-6 relative overflow-hidden" aria-label="CTA区域">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20of%20someone%20happily%20reading%20on%20a%20tablet%2C%20cozy%20indoor%20setting%2C%20warm%20lighting%2C%20anime%20inspired%20art%2C%20soft%20colors%2C%20wide%20composition%2C%20comfortable%20atmosphere&width=1920&height=600&seq=cta-bg-comic&orientation=landscape"
            className="absolute inset-0 w-full h-full object-cover object-top"
            alt=""
          />
          <div className="absolute inset-0 bg-accent-950/85"></div>
          <div className="relative z-10 text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-bold text-white font-heading mb-3">
              喜欢这部漫画？
            </h2>
            <p className="text-white/70 text-sm md:text-base mb-8">
              立即开始阅读，沉浸在精彩的故事世界中
            </p>
            <Link
              to="/category/fantasy"
              className="inline-flex items-center gap-2 bg-primary-500 text-white dark:text-foreground-950 text-sm font-medium px-8 py-3 rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              探索更多漫画
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${comic.title} - 韩国漫画在线阅读`,
            description: comic.description,
            author: {
              '@type': 'Person',
              name: comic.author,
            },
            datePublished: comic.chapters[0]?.updateDate || '2025-01-01',
            dateModified: comic.updateDate,
            inLanguage: 'zh-CN',
            about: {
              '@type': 'Thing',
              name: comic.category,
            },
            keywords: comic.tags.join(', '),
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.example.com/' },
              { '@type': 'ListItem', position: 2, name: comic.category, item: `https://www.example.com/category/${comic.categorySlug}` },
              { '@type': 'ListItem', position: 3, name: comic.title },
            ],
          }),
        }}
      />
    </>
  );
}